import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useMilestones } from './useMilestones';
import { logEventDirect } from './useEventLogger';

export interface TryOnJob {
  id: string;
  user_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  avatar_image_url: string;
  avatar_type: string;
  clothing_items: {
    id: string;
    name: string;
    image: string;
    price?: number;
    currency?: string;
    store?: string;
    url?: string;
  }[];
  result_image_url: string | null;
  result_look_id: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

// Event emitter for job completion notifications
type JobCompletionCallback = (job: TryOnJob) => void;
const completionCallbacks: Set<JobCompletionCallback> = new Set();

// Track job IDs that have already been notified to prevent duplicates
const notifiedJobIds: Set<string> = new Set();

export function onTryOnJobComplete(callback: JobCompletionCallback) {
  completionCallbacks.add(callback);
  return () => completionCallbacks.delete(callback);
}

export function useTryOnJobs() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [pendingCount, setPendingCount] = useState(0);
  const [hasNewCompletion, setHasNewCompletion] = useState(false);
  const previousJobsRef = useRef<TryOnJob[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { trackMilestone } = useMilestones();

  // Fetch user's pending/recent jobs
  const { data: jobs = [], isLoading, refetch } = useQuery({
    queryKey: ['tryon-jobs', user?.id],
    queryFn: async ({ signal }) => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('tryon_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10)
        .abortSignal(signal);

      if (error) throw error;

      const fetchedJobs = (data || []).map(row => ({
        ...row,
        clothing_items: (Array.isArray(row.clothing_items) ? row.clothing_items : []) as TryOnJob['clothing_items'],
        status: row.status as TryOnJob['status'],
      })) as TryOnJob[];

      // Auto-expire stale jobs to prevent permanently stuck UI
      const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
      const now = Date.now();
      for (const job of fetchedJobs) {
        if (
          (job.status === 'pending' || job.status === 'processing') &&
          now - new Date(job.created_at).getTime() > STALE_THRESHOLD_MS
        ) {
          // Fire-and-forget DB update
          supabase.from('tryon_jobs').update({
            status: 'failed' as const,
            error_message: 'Job timed out',
            completed_at: new Date().toISOString(),
          }).eq('id', job.id).then(() => {});
          // Override locally so UI updates immediately
          job.status = 'failed';
          job.error_message = 'Job timed out';
        }
      }

      return fetchedJobs;
    },
    enabled: !!user,
    staleTime: 30_000, // Keep data fresh for 30s to prevent loading flash on back-navigation
    gcTime: 5 * 60_000, // Keep in cache for 5 minutes
    refetchInterval: (query) => {
      // Poll every 5s when there are pending jobs, otherwise stop
      const jobs = query.state.data as TryOnJob[] | undefined;
      const hasPending = jobs?.some(j => j.status === 'pending' || j.status === 'processing');
      return hasPending ? 5_000 : false;
    },
  });

  // Count pending jobs and detect completions
  // Use a ref to track whether this instance has already processed a given data snapshot
  const lastProcessedDataRef = useRef<string>('');
  
  useEffect(() => {
    const pending = jobs.filter(j => j.status === 'pending' || j.status === 'processing').length;
    setPendingCount(pending);

    // Create a fingerprint of current job statuses to deduplicate across hook instances
    const dataFingerprint = jobs.map(j => `${j.id}:${j.status}`).join(',');
    if (dataFingerprint === lastProcessedDataRef.current) {
      return; // Same data snapshot, skip processing
    }
    lastProcessedDataRef.current = dataFingerprint;

    // Check for newly completed jobs
    const previousJobs = previousJobsRef.current;
    if (previousJobs.length > 0) {
      jobs.forEach(job => {
        if (job.status === 'completed') {
          // Safety net: skip jobs that don't belong to the current user
          if (job.user_id !== user?.id) return;
          const prevJob = previousJobs.find(p => p.id === job.id);
          if (prevJob && prevJob.status !== 'completed') {
            // Atomically check-and-set to prevent duplicates across multiple hook instances
            if (notifiedJobIds.has(job.id)) {
              return;
            }
            // Mark IMMEDIATELY before any async work to prevent race conditions
            notifiedJobIds.add(job.id);
            
            // Only notify if job was completed recently (within last 2 minutes)
            if (job.completed_at) {
              const completedAt = new Date(job.completed_at).getTime();
              const now = Date.now();
              const ageSeconds = (now - completedAt) / 1000;
              if (ageSeconds > 120) {
                return;
              }
            }
            
            // This job just completed - notify all listeners
            setHasNewCompletion(true);
            completionCallbacks.forEach(cb => cb(job));
            setTimeout(() => setHasNewCompletion(false), 3000);
            
            // Clean up old job IDs after 5 minutes to prevent memory leak
            setTimeout(() => notifiedJobIds.delete(job.id), 5 * 60 * 1000);
          }
        }
      });
    }
    previousJobsRef.current = jobs;
  }, [jobs]);

  // Subscribe to realtime updates for job status changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`tryon-jobs-updates-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tryon_jobs',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Force immediate refetch (not just invalidate) to catch completions quickly
          refetch();
          queryClient.invalidateQueries({ queryKey: ['tryon-looks', user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      // Abort any pending edge function calls on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [user, queryClient]);

  // Clear new completion flag
  const clearNewCompletion = useCallback(() => {
    setHasNewCompletion(false);
  }, []);

  // Create a new background job
  const createJob = useMutation({
    mutationFn: async (params: {
      avatarImageUrl: string;
      avatarType: string;
      clothingItems: TryOnJob['clothing_items'];
    }) => {
      if (!user) throw new Error('Not authenticated');

      // Use type assertion for the insert
      const insertData = {
        user_id: user.id,
        avatar_image_url: params.avatarImageUrl,
        avatar_type: params.avatarType,
        clothing_items: params.clothingItems,
        quality: 'hd',
        status: 'pending' as const,
      };

      const { data, error } = await supabase
        .from('tryon_jobs')
        .insert(insertData as any)
        .select()
        .single();

      if (error) throw error;

      // Cancel any previous pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      
      // Trigger the background processing
      supabase.functions.invoke('virtual-tryon', {
        body: {
          jobId: data.id,
          async: true,
        },
      }).catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Failed to trigger background processing:', err);
        }
      });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tryon-jobs', user?.id] });
      // Track first try-on milestone
      const itemsCount = Array.isArray(data.clothing_items) ? data.clothing_items.length : 0;
      trackMilestone('first_tryon', { items_count: itemsCount });

      // Log event for analytics
      logEventDirect('tryon_started', 'ai_feature', {
        items_count: itemsCount,
        avatar_type: data.avatar_type,
      }, user?.id);

      // Update persona signals for try-on (fire and forget)
      supabase.functions.invoke('update-persona-signal', {
        body: {
          type: 'tryon',
          data: { items_count: itemsCount },
        },
      }).catch(console.error);
    },
  });

  // Cancel a pending job
  const cancelJob = useMutation({
    mutationFn: async (jobId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('tryon_jobs')
        .update({ 
          status: 'failed', 
          error_message: 'Cancelled by user',
          completed_at: new Date().toISOString(),
        })
        .eq('id', jobId)
        .eq('user_id', user.id)
        .in('status', ['pending', 'processing']);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tryon-jobs', user?.id] });
    },
  });

  // Cancel all pending jobs
  const cancelAllJobs = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('tryon_jobs')
        .update({ 
          status: 'failed', 
          error_message: 'Cancelled by user',
          completed_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .in('status', ['pending', 'processing']);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tryon-jobs', user?.id] });
    },
  });

  // Get the most recently completed job
  const latestCompletedJob = jobs.find(j => j.status === 'completed');

  // Get pending jobs for display
  const pendingJobs = jobs.filter(j => j.status === 'pending' || j.status === 'processing');

  return {
    jobs,
    pendingJobs,
    pendingCount,
    isLoading,
    refetch,
    createJob,
    cancelJob,
    cancelAllJobs,
    latestCompletedJob,
    hasPendingJobs: pendingCount > 0,
    hasNewCompletion,
    clearNewCompletion,
  };
}
